import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, Empty, Space, Row, Col, Spin, Typography, Tag, Collapse, Button, Alert, List } from "antd";
import { ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import pushPriority from "../../services/pushPriority";

const { Text } = Typography;
const { Panel } = Collapse;

interface PushData {
  id: string;
  cnj: string;
  data: any;
  loading: boolean;
  error: string | null;
  notFound: boolean;
  isCapturing: boolean; // Se ainda está sendo capturado
}

/**
 * Componente para visualizar um push individual e monitorar seu status.
 */
const PushViewer: React.FC<{ pushId: string; cnj: string }> = ({ pushId, cnj }) => {
  const [pushData, setPushData] = useState<PushData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Carrega os dados do push.
   */
  const loadPush = useCallback(async () => {
    try {
      setLoading(true);
      const response = await pushPriority.getDados({ cnjId: pushId });

      // Verifica se não foi encontrado
      if (!response || !response.success || !response.data) {
        setPushData({
          id: pushId,
          cnj,
          data: null,
          loading: false,
          error: "Push não encontrado",
          notFound: true,
          isCapturing: false,
        });
        return;
      }

      const data = response.data;
      
      // Verifica se ainda está sendo capturado
      // Um push está sendo capturado se tem info com status pending ou se não tem info ainda
      const info = data.info || [];
      const hasPending = info.some((item: any) => {
        const lastStatus = item.lastStatus;
        return lastStatus === 'pending' || lastStatus === null || lastStatus === undefined;
      });
      const isCapturing = hasPending || info.length === 0;

      setPushData({
        id: pushId,
        cnj,
        data,
        loading: false,
        error: null,
        notFound: false,
        isCapturing,
      });

      // Se ainda está sendo capturado, continua monitorando
      if (isCapturing && !intervalRef.current) {
        intervalRef.current = setInterval(() => {
          loadPush();
        }, 60000); // 1 minuto
      } else if (!isCapturing && intervalRef.current) {
        // Se não está mais sendo capturado, para o monitoramento
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } catch (err: any) {
      console.error("Erro ao buscar push:", err);
      setPushData({
        id: pushId,
        cnj,
        data: null,
        loading: false,
        error: err.response?.data?.message || err.message || "Erro ao buscar push",
        notFound: false,
        isCapturing: false,
      });
      
      // Para o monitoramento em caso de erro
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } finally {
      setLoading(false);
    }
  }, [pushId, cnj]);

  useEffect(() => {
    loadPush();

    // Cleanup: limpa o intervalo quando o componente é desmontado
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [loadPush]);

  if (loading && !pushData) {
    return (
      <Collapse>
        <Panel
          header={
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Text copyable code style={{ fontSize: "14px" }}>
                {pushId}
              </Text>
              <Text type="secondary">({cnj})</Text>
              <Spin size="small" />
            </div>
          }
          key={pushId}
          disabled
        >
          {/* Conteúdo vazio durante loading */}
        </Panel>
      </Collapse>
    );
  }

  if (!pushData) {
    return null;
  }

  if (pushData.notFound || pushData.error) {
    return (
      <Collapse>
        <Panel
          header={
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Text copyable code style={{ fontSize: "14px" }}>
                {pushId}
              </Text>
              <Text type="secondary">({cnj})</Text>
              <Tag color="error" icon={<CloseCircleOutlined />}>
                {pushData.notFound ? "Não encontrado" : "Erro"}
              </Tag>
            </div>
          }
          key={pushId}
        >
          {/* Conteúdo vazio para pushes não encontrados */}
        </Panel>
      </Collapse>
    );
  }

  const info = pushData.data.info || [];
  const statusCounts = {
    success: 0,
    error: 0,
    warning: 0,
    pending: 0,
  };

  info.forEach((item: any) => {
    const status = item.lastStatus;
    if (status === 'success') statusCounts.success++;
    else if (status === 'error') statusCounts.error++;
    else if (status === 'warning') statusCounts.warning++;
    else if (status === 'pending' || !status) statusCounts.pending++;
  });

  const totalInfo = info.length;
  const completedInfo = statusCounts.success + statusCounts.error + statusCounts.warning;
  const isCapturing = pushData.isCapturing;

  return (
    <Collapse>
      <Panel
        header={
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <Text copyable code style={{ fontSize: "14px" }}>
              {pushId}
            </Text>
            <Text type="secondary">({cnj})</Text>
            {isCapturing ? (
              <>
                <Tag color="processing" icon={<ClockCircleOutlined />}>
                  Capturando...
                </Tag>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {completedInfo}/{totalInfo} finalizadas
                </Text>
              </>
            ) : (
              <Tag color="success" icon={<CheckCircleOutlined />}>
                Finalizado
              </Tag>
            )}
          </div>
        }
        key={pushId}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Text strong>Status das Abrangências:</Text>
            <Space style={{ marginTop: 8 }}>
              {statusCounts.success > 0 && (
                <Tag color="success">Sucesso: {statusCounts.success}</Tag>
              )}
              {statusCounts.error > 0 && (
                <Tag color="error">Erro: {statusCounts.error}</Tag>
              )}
              {statusCounts.warning > 0 && (
                <Tag color="warning">Aviso: {statusCounts.warning}</Tag>
              )}
              {statusCounts.pending > 0 && (
                <Tag color="processing">Pendente: {statusCounts.pending}</Tag>
              )}
            </Space>
          </div>

          {info.length > 0 && (
            <List
              size="small"
              dataSource={info}
              renderItem={(item: any) => {
                const status = item.lastStatus || 'pending';
                const statusColor = status === 'success' 
                  ? 'success' 
                  : status === 'error' 
                  ? 'error' 
                  : status === 'warning'
                  ? 'warning'
                  : 'processing';

                return (
                  <List.Item>
                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                      <Space>
                        <Tag color="cyan">{item.platformTag}</Tag>
                        <Tag color="volcano">{item.searchTag}</Tag>
                        <Tag color="purple">{item.queryTag}</Tag>
                        <Tag color={statusColor}>{status || 'pending'}</Tag>
                      </Space>
                      {item.lastResponse && (
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          Última resposta: {new Date(item.lastResponse).toLocaleString()}
                        </Text>
                      )}
                    </Space>
                  </List.Item>
                );
              }}
            />
          )}
        </Space>
      </Panel>
    </Collapse>
  );
};

/**
 * Página de monitoramento de pushes.
 *
 * Exibe todos os pushes que estão sendo monitorados e atualiza a cada 1 minuto.
 */
const PushMonitoringContent: React.FC = () => {
  const navigate = useNavigate();
  const [pushIds, setPushIds] = useState<Array<{ id: string; cnj: string }>>([]);

  useEffect(() => {
    // Carregar IDs do localStorage
    try {
      const storedIds = localStorage.getItem('pushMonitoringIds');
      const storedCnjs = localStorage.getItem('pushMonitoringCnjs');
      
      if (storedIds && storedCnjs) {
        const ids = JSON.parse(storedIds);
        const cnjs = JSON.parse(storedCnjs);
        
        // Combinar IDs e CNJs
        const combined = ids.map((id: string) => {
          const cnjData = cnjs.find((c: any) => c.id === id);
          return {
            id,
            cnj: cnjData?.cnj || id,
          };
        });
        
        setPushIds(combined);
      }
    } catch (error) {
      console.error('Erro ao carregar push IDs:', error);
    }
  }, []);

  const handleBack = () => {
    navigate("/push");
  };

  const handleRefresh = () => {
    // Forçar reload de todos os componentes
    window.location.reload();
  };

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <PageHeader title="Monitoramento de Push" />
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              Atualizar
            </Button>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              Voltar
            </Button>
          </Space>
        </div>

        <Alert
          message="Monitoramento Ativo"
          description="Os pushes estão sendo monitorados a cada 1 minuto. Pushes que ainda estão sendo capturados continuarão sendo monitorados até que todas as abrangências sejam finalizadas."
          type="info"
          showIcon
        />

        {pushIds.length === 0 ? (
          <Empty 
            description="Nenhum push sendo monitorado. Volte para a página de Novo Push para criar." 
            image={Empty.PRESENTED_IMAGE_SIMPLE} 
          />
        ) : (
          <Row gutter={[16, 16]}>
            {pushIds.map((push, idx) => (
              <Col xs={24} sm={24} md={24} lg={24} xl={24} key={idx}>
                <PushViewer pushId={push.id} cnj={push.cnj} />
              </Col>
            ))}
          </Row>
        )}
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página PushMonitoring.
 */
const PushMonitoring: React.FC = () => {
  return (
    <PushMonitoringContent />
  );
};

export default PushMonitoring;
